import { ChevronDown, ChevronRight } from "lucide-react";
import React, { useState } from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CalendarTreeRows } from "@/pages/cashflow/CalendarTreeRows";
import {
  CELL_PAD,
  NUM_CELL,
  filterEntries,
  formatRubCompact,
  isoWeekNumber,
  isZeroAmount,
  stickyCell,
  sumDecimalStrings,
  weekHeader,
  weekLabel,
  type CashflowCellSelection,
} from "@/pages/cashflow/utils";
import type { CashflowEntry, CashflowOrgCalendar, CashflowRow } from "@/types/api";

interface Props {
  calendar: CashflowOrgCalendar;
  weeks: string[];
  /** Все записи горизонта (`calendar.entries` с бэка) — для раскрытия строк. */
  entries: CashflowEntry[];
  /** ИНН выбранной вкладки; `ALL_ORGS` — без фильтра по организации. */
  orgInn: string;
  /** Клик по ячейке недели — открыть детализацию записей. */
  onCellClick?: (sel: CashflowCellSelection) => void;
}

/** Кнопка детализации: на всю ячейку, без рамки и фона — «ячейка», но настоящая кнопка. */
const CELL_BUTTON =
  `block w-full h-full ${CELL_PAD} text-right tabular-nums rounded-sm hover:bg-muted ` +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset";

function MoneyCell({
  value,
  className = "",
  belowZero = false,
  onClick,
  hint,
}: {
  value: string | undefined;
  className?: string;
  belowZero?: boolean;
  onClick?: () => void;
  /** Подсказка кнопки детализации (`title` + скрытый суффикс доступного имени). */
  hint?: string;
}): React.ReactElement {
  // `value === undefined` — рассинхрон длины массивов с бэком: показываем «—»,
  // а не подставляем ноль, чтобы расхождение было видно.
  const muted = isZeroAmount(value) ? "text-muted-foreground" : "";
  const danger = belowZero ? "bg-red-100 text-red-700 font-semibold" : "";
  return (
    <TableCell
      {...(belowZero ? { "data-below-zero": "true" } : {})}
      // Кликабельная ячейка отдаёт отступы кнопке — кликается вся площадь.
      className={`${NUM_CELL} ${muted} ${danger} ${onClick ? "p-0" : CELL_PAD} ${className}`}
    >
      {onClick ? (
        // Доступное имя = сумма + подсказка; сумму скринридеру не заменяем.
        <button type="button" onClick={onClick} title={hint} className={CELL_BUTTON}>
          {formatRubCompact(value)}
          <span className="sr-only">{hint === undefined ? "" : ` — ${hint}`}</span>
        </button>
      ) : (
        formatRubCompact(value)
      )}
    </TableCell>
  );
}

function SectionRow({
  label,
  cells,
  total,
  emphasis,
  weeks,
  belowZero,
  onCellClick,
  expandable,
}: {
  label: string;
  cells: string[];
  total: string | undefined;
  emphasis: boolean;
  weeks: string[];
  belowZero?: boolean[];
  /** Не задан — ячейки этой строки не кликабельны («Остаток на начало»). */
  onCellClick?: (weekIndex: number) => void;
  /** Строка статьи с ненулевым итогом раскрывается до контрагентов. */
  expandable?: { expanded: boolean; onToggle: () => void };
}): React.ReactElement {
  const zeroRow = !emphasis && total !== undefined && isZeroAmount(total);
  const rowCls = [
    // `group` — чтобы липкие ячейки подсвечивались вместе со строкой, а
    // `hover:bg-muted` (вместо базового `hover:bg-muted/50`) — чтобы цвет
    // строки и липкой ячейки совпадал: полупрозрачный под липкой нельзя.
    "group hover:bg-muted",
    emphasis ? "font-semibold bg-muted" : "",
    zeroRow ? "text-muted-foreground" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const Chevron = expandable?.expanded === true ? ChevronDown : ChevronRight;
  return (
    <TableRow className={rowCls}>
      <TableCell className={`${stickyCell("left", emphasis)} ${CELL_PAD}`}>
        {expandable === undefined ? (
          label
        ) : (
          <button
            type="button"
            aria-expanded={expandable.expanded}
            aria-label={`${expandable.expanded ? "Свернуть" : "Раскрыть"} ${label}`}
            onClick={expandable.onToggle}
            className="inline-flex items-center gap-1 rounded-sm text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Chevron className="h-3 w-3 shrink-0" aria-hidden="true" />
            {label}
          </button>
        )}
      </TableCell>
      {weeks.map((w, i) => (
        <MoneyCell
          key={w}
          value={cells[i]}
          belowZero={belowZero?.[i] === true}
          {...(onCellClick
            ? {
                onClick: () => onCellClick(i),
                hint: `показать записи: ${label}, неделя ${weekLabel(w)}`,
              }
            : {})}
        />
      ))}
      <MoneyCell value={total} className={stickyCell("right", emphasis)} />
    </TableRow>
  );
}

/** Раскрытые узлы дерева: ключи `category` и `category|kontragent`. */
interface ExpandedState {
  /** Вкладка, на которой их раскрыли: сменилась — раскрытие сбрасывается. */
  org: string;
  keys: Set<string>;
}

const NO_KEYS: Set<string> = new Set();

export function CalendarTable({
  calendar,
  weeks,
  entries,
  orgInn,
  onCellClick,
}: Props): React.ReactElement {
  const inRows: CashflowRow[] = calendar.rows.filter((r) => r.direction === "in");
  const outRows: CashflowRow[] = calendar.rows.filter((r) => r.direction === "out");
  // Остаток на начало недели: для первой — `opening`, дальше — конец прошлой.
  const openingCells = weeks.map((_, i) =>
    i === 0 ? calendar.opening : (calendar.closing[i - 1] ?? ""),
  );
  /** Обработчик ячеек строки; без `onCellClick` строка остаётся некликабельной. */
  const cellHandler = (sel: Omit<CashflowCellSelection, "weekIndex">) =>
    onCellClick ? (weekIndex: number) => onCellClick({ ...sel, weekIndex }) : undefined;

  const [expanded, setExpanded] = useState<ExpandedState>({ org: orgInn, keys: NO_KEYS });
  // Смена вкладки — раскрытие ни при чём: у другой организации другие суммы.
  const keys = expanded.org === orgInn ? expanded.keys : NO_KEYS;
  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev.org === orgInn ? prev.keys : NO_KEYS);
      if (!next.delete(key)) next.add(key);
      return { org: orgInn, keys: next };
    });

  const categoryRow = (r: CashflowRow): React.ReactElement => {
    // Пустая строка раскрывать нечего — шеврон только у ненулевых.
    const canExpand = !isZeroAmount(r.total);
    const isOpen = canExpand && keys.has(r.category);
    return (
      <React.Fragment key={r.category}>
        <SectionRow
          label={r.label}
          cells={r.cells}
          total={r.total}
          emphasis={false}
          weeks={weeks}
          onCellClick={cellHandler({ category: r.category, direction: r.direction })}
          {...(canExpand
            ? { expandable: { expanded: isOpen, onToggle: () => toggle(r.category) } }
            : {})}
        />
        {isOpen && (
          <CalendarTreeRows
            category={r.category}
            rowCells={r.cells}
            weeks={weeks}
            entries={filterEntries(entries, { category: r.category, orgInn })}
            expanded={keys}
            onToggle={toggle}
          />
        )}
      </React.Fragment>
    );
  };

  return (
    <Table className="min-w-[760px] text-xs">
      <TableHeader>
        {/* Без `group`: заголовок не подсвечивается, а липкие ячейки в нём — тем более. */}
        <TableRow className="hover:bg-transparent">
          <TableHead className={`${stickyCell("left", false)} ${CELL_PAD} h-8 min-w-[200px]`}>
            Статья
          </TableHead>
          {weeks.map((w) => (
            <TableHead
              key={w}
              // Номер недели сверху мелко, даты снизу — так колонка ýже.
              title={weekHeader(w)}
              className={`${CELL_PAD} h-8 text-right whitespace-nowrap leading-tight`}
            >
              <span className="block text-[10px] font-normal">W{isoWeekNumber(w)}</span>
              <span className="block">{weekLabel(w)}</span>
            </TableHead>
          ))}
          <TableHead className={`${stickyCell("right", false)} ${CELL_PAD} h-8 text-right`}>
            Итого, ₽
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <SectionRow
          label="Остаток на начало"
          cells={openingCells}
          total={undefined}
          emphasis
          weeks={weeks}
        />
        {inRows.map(categoryRow)}
        <SectionRow
          label="Итого приход"
          cells={calendar.inflow}
          total={sumDecimalStrings(calendar.inflow)}
          emphasis
          weeks={weeks}
          onCellClick={cellHandler({ category: null, direction: "in" })}
        />
        {outRows.map(categoryRow)}
        <SectionRow
          label="Итого расход"
          cells={calendar.outflow}
          total={sumDecimalStrings(calendar.outflow)}
          emphasis
          weeks={weeks}
          onCellClick={cellHandler({ category: null, direction: "out" })}
        />
        <SectionRow
          label="Остаток на конец"
          cells={calendar.closing}
          total={undefined}
          emphasis
          weeks={weeks}
          belowZero={calendar.below_zero}
          onCellClick={cellHandler({ category: null, direction: null })}
        />
      </TableBody>
    </Table>
  );
}
